import { useEffect, useRef, useState } from 'react';
import { Keyboard, Platform } from 'react-native';
import { scheduleIosUiPresent } from '@/utils/scheduleIosUiPresent';

/**
 * Bridge an external "open" flag to Modal `visible` with iOS-safe presentation timing.
 * Nested RN Modals on iOS often fail to present when `visible` flips synchronously
 * from inside another Modal — defer via scheduleIosUiPresent and portal to root.
 */
export function useIosOverlayPresent(requestedVisible: boolean): boolean {
  const [presentedVisible, setPresentedVisible] = useState(false);
  const presentCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      presentCleanupRef.current?.();
      presentCleanupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!requestedVisible) {
      presentCleanupRef.current?.();
      presentCleanupRef.current = null;
      setPresentedVisible(false);
      return;
    }

    Keyboard.dismiss();
    presentCleanupRef.current?.();
    presentCleanupRef.current = scheduleIosUiPresent(() => {
      setPresentedVisible(true);
    });

    return () => {
      presentCleanupRef.current?.();
      presentCleanupRef.current = null;
    };
  }, [requestedVisible]);

  return Platform.OS === 'ios' ? presentedVisible : requestedVisible;
}
